import 'antd/dist/reset.css';

import React, { useState, useEffect } from 'react';
import { WCCustomer } from '../../types/wordpress';
import { WordPressAPI } from '../../services/api/api';
import CustomerForm from './CustomerForm';
import { Button, Table, Modal, message, Popconfirm, Tag, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, DeleteOutlined, UndoOutlined, SearchOutlined } from '@ant-design/icons';
import './styles.css';

const CustomerList: React.FC = () => {
    const [customers, setCustomers] = useState<WCCustomer[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState<WCCustomer | null>(null);
    const [searchText, setSearchText] = useState('');
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        fetchCustomers();
    }, [currentPage, pageSize, searchText]);

    const fetchCustomers = async () => {
        try {
            const data = await WordPressAPI.getCustomers({
                page: currentPage,
                per_page: pageSize,
                search: searchText
            });
            setCustomers(data.items);
            setTotal(data.total);
        } catch (error) {
            message.error('Failed to fetch customers');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await WordPressAPI.updateCustomer(id, { is_deleted: true });
            message.success('Customer deactivated successfully');
            fetchCustomers();
        } catch (error) {
            message.error('Failed to deactivate customer');
            console.error(error);
        }
    };

    const handleRestore = async (id: number) => {
        try {
            await WordPressAPI.updateCustomer(id, { is_deleted: false });
            message.success('Customer restored successfully');
            fetchCustomers();
        } catch (error) {
            message.error('Failed to restore customer');
            console.error(error);
        }
    };

    const handleEdit = (customer: WCCustomer) => {
        setEditingCustomer(customer);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingCustomer(null);
    };

    const handleFormSubmit = async (values: Partial<WCCustomer>) => {
        try {
            if (editingCustomer?.id) {
                await WordPressAPI.updateCustomer(editingCustomer.id, values);
                message.success('Customer updated successfully');
            } else {
                await WordPressAPI.createCustomer(values);
                message.success('Customer created successfully');
            }
            handleModalClose();
            fetchCustomers();
        } catch (error) {
            message.error('Failed to save customer');
            console.error(error);
        }
    };

    const handleCreate = () => {
        setEditingCustomer(null);
        setIsModalVisible(true);
    };

    const columns: ColumnsType<WCCustomer> = [
        {
            title: 'Customer Name',
            dataIndex: 'first_name',
            key: 'first_name',
            render: (first_name) => first_name || 'Unnamed Customer',
            sorter: (a, b) => (a.first_name || '').localeCompare(b.first_name || ''),
        },
        {
            title: 'Contact Information',
            key: 'contact',
            render: (_, record) => {
                const defaultContact = record.contacts?.find(contact => contact.is_default) || record.contacts?.[0];
                return defaultContact ? (
                    <div>
                        <div><strong>{defaultContact.contact_name}</strong></div>
                        <div>{defaultContact.phone_number}</div>
                        <div>{defaultContact.email}</div>
                        {defaultContact.is_default && <Tag color="blue">Default Contact</Tag>}
                    </div>
                ) : (
                    <span>No contact information</span>
                );
            },
        },
        {
            title: 'Address',
            key: 'address',
            render: (_, record) => (
                <div>
                    <div>{record.address}</div>
                    <div>{`${record.city}, ${record.state} ${record.zip_code}`}</div>
                </div>
            ),
        },
        {
            title: 'FDA',
            dataIndex: 'fda',
            key: 'fda',
        },
        {
            title: 'Profit/Discount',
            key: 'profit',
            render: (_, record) => (
                <div>
                    <div>Order Profit: {record.order_profit_percentage}%</div>
                    <div>Item Profit: {record.item_profit_percentage}%</div>
                    <div>Discount: {record.discount_percentage}%</div>
                    <div>Cashback: {record.cashback_percentage}%</div>
                </div>
            ),
        },
        {
            title: 'Status',
            key: 'is_deleted',
            filters: [
                { text: 'Active', value: false },
                { text: 'Deleted', value: true },
            ],
            onFilter: (value, record) => record.is_deleted === value,
            render: (_, record) => (
                record.is_deleted ?
                    <Tag color="red">Disabled</Tag> :
                    <Tag color="green">Active</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <span className="action-buttons">
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    {record.is_deleted ? (
                        <Popconfirm
                            title="Restore this customer?"
                            onConfirm={() => handleRestore(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="primary"
                                ghost
                                icon={<UndoOutlined />}
                                style={{ color: '#52c41a', borderColor: '#52c41a' }}
                                className="restore-button"
                            >
                                Restore
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Deactivate this customer?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                ghost
                                icon={<DeleteOutlined />}
                                className="delete-button"
                            >
                                Deactivate
                            </Button>
                        </Popconfirm>
                    )}
                </span>
            ),
        },
    ];

    return (
        <div className="customer-list">
            <div className="customer-list-header">
                <h2>Customers</h2>
                <div className="header-actions">
                    <Input
                        placeholder="Search customers..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: 300 }}
                        allowClear
                    />
                    <Button
                        type="primary"
                        onClick={handleCreate}
                    >
                        Add New Customer
                    </Button>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={customers}
                loading={loading}
                rowKey="id"
                rowClassName={(record) => record.is_deleted ? 'opacity-50' : ''}
                pagination={{
                    total: total,
                    current: currentPage,
                    pageSize: pageSize,
                    onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    },
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
            />

            <Modal
                title={editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={900}
                destroyOnClose={true}
            >
                <CustomerForm
                    key={editingCustomer?.id || 'new'}
                    initialValues={editingCustomer}
                    onSubmit={handleFormSubmit}
                    onCancel={handleModalClose}
                />
            </Modal>
        </div>
    );
};

export default CustomerList;